import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Badge, Button, Card, Col, Empty, List, Progress, Row, Skeleton,
  Select, Space, Statistic, Table, Tag, Typography
} from 'antd';
import {
  AlertOutlined, AuditOutlined, BankOutlined, BellOutlined, CheckCircleOutlined,
  CheckSquareOutlined, ClockCircleOutlined, DesktopOutlined, EnvironmentOutlined,
  FileDoneOutlined, IdcardOutlined, LinkOutlined, ReloadOutlined,
  RightOutlined, SafetyCertificateOutlined, TeamOutlined, UserAddOutlined,
  UserOutlined, WarningOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { schoolAPI } from '../../services/api';
import { normalizeScopeId, resolveDefaultSchoolId, shouldLockSchoolSelection } from '../../utils/schoolScope';

const { Title, Text } = Typography;
const { Option } = Select;
const priorityMeta = {
  normal: { text: '普通', color: 'blue' },
  important: { text: '重要', color: 'orange' },
  urgent: { text: '紧急', color: 'red' }
};
const alertMeta = {
  info: { color: 'blue', icon: <AlertOutlined /> },
  warning: { color: 'orange', icon: <WarningOutlined /> },
  critical: { color: 'red', icon: <WarningOutlined /> }
};

const schoolRegion = (school) => [school?.province, school?.city, school?.county].filter(Boolean).join(' / ') || '地区待完善';

export default function SchoolAdminDashboard({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dashboard, setDashboard] = useState(null);
  const [schools, setSchools] = useState([]);
  const [selectedSchoolId, setSelectedSchoolId] = useState(null);

  const loadDashboard = async () => {
    setLoading(true);
    setError('');
    try {
      if (!selectedSchoolId) return;
      const response = await api.get('/school-admin/dashboard', { params: { schoolId: selectedSchoolId } });
      setDashboard(response.data?.data || null);
    } catch (requestError) {
      setError(requestError.response?.data?.message || '学校管理驾驶舱加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    schoolAPI.getAll().then((response) => {
      const availableSchools = response.data?.data || [];
      setSchools(availableSchools);
      setSelectedSchoolId(resolveDefaultSchoolId(user, availableSchools));
    }).catch((requestError) => {
      setError(requestError.response?.data?.message || '授权学校加载失败');
      setLoading(false);
    });
  }, [user]);

  useEffect(() => {
    if (selectedSchoolId) loadDashboard();
  }, [selectedSchoolId]); // eslint-disable-line react-hooks/exhaustive-deps

  const school = dashboard?.school || {};
  const stats = dashboard?.organizationStats || {};
  const pending = dashboard?.pendingActions || {};
  const coverage = dashboard?.dataCoverage || {};
  const taskProgress = dashboard?.taskProgress || {};
  const noticeStats = dashboard?.noticeStats || {};
  const today = dashboard?.todayOperations || {};
  const alerts = dashboard?.riskAlerts || [];
  const notices = dashboard?.recentNotices || [];
  const tasks = dashboard?.recentTasks || [];
  const trends = dashboard?.activityTrend || [];
  const capabilities = dashboard?.capabilities || {};

  const readiness = useMemo(() => {
    const core = [
      (school.profileMissing || []).length === 0,
      Number(stats.classes || 0) > 0,
      Number(stats.teachers || 0) > 0,
      Number(stats.students || 0) > 0,
      Number(coverage.parentBindingRate || 0) >= 60,
      Number(coverage.deviceCoverageRate || 0) >= 60,
      Number(coverage.scheduleCoverageRate || 0) >= 60
    ];
    return Math.round(core.filter(Boolean).length / core.length * 100);
  }, [school.profileMissing, stats, coverage]);

  if (loading) return <Card><Skeleton active paragraph={{ rows: 12 }} /></Card>;
  if (error) return <Alert type="error" showIcon message={error} action={<Button icon={<ReloadOutlined />} onClick={loadDashboard}>重新加载</Button>} />;

  const gradeColumns = [
    { title: '年级', dataIndex: 'grade', render: (value) => <Text strong>{value}</Text> },
    { title: '班级', dataIndex: 'classCount', width: 90, render: (value) => `${value || 0} 个` },
    { title: '学生', dataIndex: 'studentCount', width: 100, render: (value) => `${value || 0} 人` },
    { title: '在线班级', dataIndex: 'onlineClasses', width: 110, render: (value, row) => `${value || 0} / ${row.classCount || 0}` }
  ];

  const pendingCards = [
    { key: 'approvals', title: '待审核入校', value: Number(pending.pendingMemberships || 0) + Number(pending.pendingAffiliations || 0), icon: <AuditOutlined />, route: '/school-affiliation-corrections/review', color: '#f5222d' },
    { key: 'teacher-unbound', title: '未分班教师', value: pending.teachersWithoutClass || 0, icon: <IdcardOutlined />, route: '/teachers', color: '#fa8c16' },
    { key: 'class-owner', title: '无负责教师班级', value: pending.classesWithoutTeacher || 0, icon: <TeamOutlined />, route: '/classes', color: '#fa541c' },
    { key: 'tasks', title: '逾期学校任务', value: pending.overdueTasks || 0, icon: <ClockCircleOutlined />, route: '/school-operations', color: '#722ed1' }
  ];

  const summaryGroups = [
    {
      key: 'people',
      title: '师生规模',
      icon: <TeamOutlined />,
      color: '#1677ff',
      items: [
        { label: '全校教师', value: stats.teachers || 0, meta: `管理员 ${stats.schoolAdmins || 0}` },
        { label: '全校学生', value: stats.students || 0, meta: `家长账号 ${stats.parents || 0}` }
      ]
    },
    {
      key: 'classes',
      title: '教学组织',
      icon: <BankOutlined />,
      color: '#13a8a8',
      items: [
        { label: '全校班级', value: stats.classes || 0, meta: `${stats.grades || 0} 个年级` },
        { label: '在线班级', value: stats.onlineClasses5m || 0, meta: `设备 ${stats.onlineDevices5m || 0}/${stats.devices || 0}` }
      ]
    },
    {
      key: 'management',
      title: '家校与管理',
      icon: <LinkOutlined />,
      color: '#fa8c16',
      items: [
        { label: '家长绑定率', value: stats.parentBindingRate || 0, suffix: '%' },
        { label: '管理待办', value: pending.total || 0, danger: Number(pending.total || 0) > 0 }
      ],
      progress: stats.parentBindingRate || 0
    }
  ];

  const todayGroups = [
    {
      key: 'content', title: '内容发布', icon: <BellOutlined />, color: '#1677ff',
      items: [{ label: '广播', value: today.broadcasts || 0 }, { label: '通知', value: today.notices || 0 }]
    },
    {
      key: 'tasks', title: '任务协作', icon: <CheckSquareOutlined />, color: '#722ed1',
      items: [{ label: '新建', value: today.tasks || 0 }, { label: '完成', value: today.taskCompletions || 0 }]
    },
    {
      key: 'people', title: '人员变化', icon: <UserAddOutlined />, color: '#13a8a8',
      items: [{ label: '教师', value: today.teachersJoined || 0 }, { label: '学生', value: today.studentsAdded || 0 }]
    },
    {
      key: 'resources', title: '资源在线', icon: <DesktopOutlined />, color: '#52c41a',
      items: [{ label: '文件', value: today.uploads || 0 }, { label: '设备', value: stats.onlineDevices5m || 0 }]
    }
  ];

  const maxTrend = Math.max(1, ...trends.map((item) => Number(item.broadcasts || 0) + Number(item.notices || 0) + Number(item.tasks || 0)));

  return (
    <div style={{ background: '#f4f6fa', margin: -24, padding: 24, minHeight: '100vh' }}>
      <Card bordered={false} styles={{ body: { padding: 0 } }} style={{ overflow: 'hidden', marginBottom: 18, boxShadow: '0 10px 30px rgba(15,42,90,.12)' }}>
        <div style={{ padding: '26px 30px', color: '#fff', background: 'linear-gradient(118deg, #102a56 0%, #174ea6 55%, #1677ff 120%)' }}>
          <Row justify="space-between" align="middle" gutter={[20, 20]}>
            <Col xs={24} lg={16}>
              <Space align="start" size={16}>
                <div style={{ width: 54, height: 54, borderRadius: 14, background: 'rgba(255,255,255,.16)', display: 'grid', placeItems: 'center' }}><BankOutlined style={{ fontSize: 29 }} /></div>
                <div>
                  <Space wrap><Title level={2} style={{ color: '#fff', margin: 0 }}>{school.name}</Title>{school.isStandard && <Tag icon={<SafetyCertificateOutlined />} color="success">全国标准学校</Tag>}</Space>
                  <div style={{ marginTop: 6, opacity: .9 }}><EnvironmentOutlined /> {schoolRegion(school)}{school.officialSchoolCode && ` · 学校代码 ${school.officialSchoolCode}`}</div>
                  <div style={{ marginTop: 8, opacity: .84 }}>你好，{user?.displayName || user?.display_name || user?.name || user?.username}。这里展示全校治理数据、管理待办和风险提醒。</div>
                </div>
              </Space>
            </Col>
            <Col xs={24} lg={8} style={{ textAlign: 'right' }}>
              <Space wrap>
                <Select
                  value={selectedSchoolId}
                  onChange={setSelectedSchoolId}
                  disabled={shouldLockSchoolSelection(user, schools)}
                  style={{ width: 220, textAlign: 'left' }}
                  placeholder="选择授权学校"
                >
                  {schools.map(item => (
                    <Option key={item.id} value={normalizeScopeId(item.id)}>{item.name}</Option>
                  ))}
                </Select>
                <Button ghost icon={<BellOutlined />} onClick={() => navigate('/school-operations')}>发布通知</Button>
                <Button type="primary" icon={<CheckSquareOutlined />} onClick={() => navigate('/school-operations')}>新建学校任务</Button>
              </Space>
            </Col>
          </Row>
        </div>
      </Card>

      {(!capabilities.attendance || !capabilities.studentMessages) && (
        <Alert
          type="info"
          showIcon
          closable
          style={{ marginBottom: 16 }}
          message="部分历史业务数据源尚未启用"
          description={`考勤明细：${capabilities.attendance ? '可用' : '未启用'}；学生联系老师：${capabilities.studentMessages ? '可用' : '未启用'}。未启用模块不会再显示为错误的 0，也不会影响其他学校数据。`}
        />
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: 18 }}>
        {summaryGroups.map((group) => (
          <Col xs={24} lg={8} key={group.key}>
            <Card bordered={false} styles={{ body: { padding: '18px 20px 16px' } }} style={{ height: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <span style={{ width: 32, height: 32, borderRadius: 9, display: 'grid', placeItems: 'center', color: group.color, background: `${group.color}12`, fontSize: 17 }}>{group.icon}</span>
                <Text strong style={{ fontSize: 15 }}>{group.title}</Text>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                {group.items.map((item, index) => (
                  <div key={item.label} style={{ paddingLeft: index ? 20 : 0, borderLeft: index ? '1px solid #edf0f5' : 'none' }}>
                    <Text type="secondary">{item.label}</Text>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, marginTop: 3 }}>
                      <span style={{ fontSize: 27, lineHeight: 1.25, fontWeight: 650, color: item.danger ? '#ff4d4f' : '#20242c' }}>{item.value}</span>
                      {item.suffix && <Text type="secondary">{item.suffix}</Text>}
                      {item.meta && <Text type="secondary" style={{ marginLeft: 'auto', paddingRight: index ? 0 : 18, whiteSpace: 'nowrap' }}>{item.meta}</Text>}
                    </div>
                  </div>
                ))}
              </div>
              {group.progress !== undefined && <Progress percent={group.progress} size="small" showInfo={false} strokeColor={group.color} style={{ margin: '11px 0 0' }} />}
            </Card>
          </Col>
        ))}
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={15}>
          <Card bordered={false} title="管理待办" extra={<Button type="link" onClick={loadDashboard}>刷新 <ReloadOutlined /></Button>}>
            <Row gutter={[12, 12]}>
              {pendingCards.map((item) => (
                <Col xs={24} sm={12} xxl={6} key={item.key}>
                  <div onClick={() => navigate(item.route)} style={{ display: 'grid', gridTemplateColumns: '42px minmax(0, 1fr) auto', alignItems: 'center', gap: 11, padding: '14px 13px', border: '1px solid #edf0f5', borderRadius: 10, cursor: 'pointer', height: '100%', background: '#fff' }}>
                    <span style={{ width: 42, height: 42, display: 'grid', placeItems: 'center', borderRadius: 10, color: item.color, background: `${item.color}10`, fontSize: 19 }}>{item.icon}</span>
                    <Text style={{ lineHeight: 1.35 }}>{item.title}</Text>
                    <Space size={8}>
                      <span style={{ fontSize: 25, lineHeight: 1, fontWeight: 650, color: Number(item.value) > 0 ? item.color : '#3f3f46' }}>{item.value}</span>
                      <RightOutlined style={{ color: '#a3a8b3', fontSize: 12 }} />
                    </Space>
                  </div>
                </Col>
              ))}
            </Row>
          </Card>
        </Col>
        <Col xs={24} xl={9}>
          <Card bordered={false} title="学校数据就绪度" extra={<Button type="link" onClick={() => navigate('/school-admin')}>详细工作台 <RightOutlined /></Button>}>
            <Row align="middle" gutter={16}>
              <Col span={9}><Progress type="dashboard" percent={readiness} size={112} /></Col>
              <Col span={15}>
                <Space direction="vertical" style={{ width: '100%' }} size={7}>
                  <div><Text type="secondary">家长绑定</Text><Progress percent={coverage.parentBindingRate || 0} size="small" /></div>
                  <div><Text type="secondary">桌面客户端覆盖</Text><Progress percent={coverage.deviceCoverageRate || 0} size="small" /></div>
                  <div><Text type="secondary">课程表覆盖</Text><Progress percent={coverage.scheduleCoverageRate || 0} size="small" /></div>
                </Space>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} xl={12}>
          <Card bordered={false} title={<Space><BellOutlined />学校通知阅读情况</Space>} extra={<Button type="link" onClick={() => navigate('/school-operations')}>通知管理 <RightOutlined /></Button>}>
            <Row gutter={12} style={{ marginBottom: 12 }}>
              <Col span={8}><Statistic title="有效通知" value={noticeStats.active || 0} /></Col>
              <Col span={8}><Statistic title="教师阅读" value={noticeStats.reads || 0} /></Col>
              <Col span={8}><Statistic title="综合阅读率" value={noticeStats.readRate || 0} suffix="%" /></Col>
            </Row>
            {notices.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无学校通知" /> : <List size="small" dataSource={notices} renderItem={(item) => <List.Item><List.Item.Meta title={<Space><Tag color={priorityMeta[item.priority]?.color}>{priorityMeta[item.priority]?.text}</Tag><Text strong>{item.title}</Text></Space>} description={<Space split="·"><Text type="secondary">{dayjs(item.publishedAt).format('MM-DD HH:mm')}</Text><Text type="secondary">已读 {item.readCount || 0} 人</Text></Space>} /></List.Item>} />}
          </Card>
        </Col>
        <Col xs={24} xl={12}>
          <Card bordered={false} title={<Space><CheckSquareOutlined />学校任务完成进度</Space>} extra={<Button type="link" onClick={() => navigate('/school-operations')}>任务管理 <RightOutlined /></Button>}>
            <Row gutter={12} style={{ marginBottom: 12 }}>
              <Col span={8}><Statistic title="教师任务记录" value={taskProgress.total || 0} /></Col>
              <Col span={8}><Statistic title="已完成" value={taskProgress.completed || 0} /></Col>
              <Col span={8}><Statistic title="完成率" value={taskProgress.completionRate || 0} suffix="%" /></Col>
            </Row>
            {tasks.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无学校任务" /> : <List size="small" dataSource={tasks} renderItem={(item) => { const total = Number(item.assigneeCount || 0); const done = Number(item.completedCount || 0); return <List.Item><List.Item.Meta title={<Space><Tag color={priorityMeta[item.priority]?.color}>{priorityMeta[item.priority]?.text}</Tag><Text strong>{item.title}</Text>{item.dueAt && dayjs(item.dueAt).isBefore(dayjs()) && done < total && <Tag color="red">逾期</Tag>}</Space>} description={<Progress percent={total ? Math.round(done / total * 100) : 0} format={() => `${done}/${total} 人`} size="small" />} /></List.Item>; }} />}
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} xl={8}>
          <Card bordered={false} title="年级与学生分布" extra={<Button type="link" onClick={() => navigate('/classes')}>班级管理 <RightOutlined /></Button>}>
            <Table size="small" rowKey="grade" pagination={false} columns={gradeColumns} dataSource={dashboard?.gradeDistribution || []} />
          </Card>
        </Col>
        <Col xs={24} xl={8}>
          <Card bordered={false} title="近7日学校运营">
            <Space direction="vertical" style={{ width: '100%' }} size={9}>
              {trends.map((item) => {
                const total = Number(item.broadcasts || 0) + Number(item.notices || 0) + Number(item.tasks || 0);
                return <div key={item.date}><Row justify="space-between"><Text type="secondary">{dayjs(item.date).format('MM-DD')}</Text><Text>{total} 项 · 完成任务 {item.completions || 0}</Text></Row><Progress percent={Math.round(total / maxTrend * 100)} showInfo={false} size="small" /></div>;
              })}
            </Space>
          </Card>
        </Col>
        <Col xs={24} xl={8}>
          <Card bordered={false} title={<Space><WarningOutlined />风险与数据提醒<Badge count={alerts.length} /></Space>}>
            {alerts.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="当前没有需要处理的风险" /> : <List size="small" dataSource={alerts} renderItem={(item) => <List.Item onClick={() => item.route && navigate(item.route)} style={{ cursor: item.route ? 'pointer' : 'default' }}><List.Item.Meta avatar={<span style={{ color: alertMeta[item.level]?.color, fontSize: 19 }}>{alertMeta[item.level]?.icon}</span>} title={<Text strong>{item.title}</Text>} description={item.description} /></List.Item>} />}
          </Card>
        </Col>
      </Row>

      <Card bordered={false} title="今日学校运营" style={{ marginTop: 16 }} styles={{ body: { paddingTop: 16 } }}>
        <Row gutter={[0, 16]}>
          {todayGroups.map((group, groupIndex) => (
            <Col xs={24} sm={12} xl={6} key={group.key}>
              <div style={{ padding: '4px 24px 6px', borderLeft: groupIndex ? '1px solid #edf0f5' : 'none' }}>
                <Space size={8} style={{ marginBottom: 10 }}>
                  <span style={{ color: group.color, fontSize: 17 }}>{group.icon}</span>
                  <Text strong>{group.title}</Text>
                </Space>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 18 }}>
                  {group.items.map((item) => (
                    <div key={item.label} style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
                      <Text type="secondary">{item.label}</Text>
                      <span style={{ fontSize: 25, lineHeight: 1, fontWeight: 650, color: '#20242c' }}>{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Col>
          ))}
        </Row>
      </Card>

      <Card bordered={false} title="学校管理快捷入口" style={{ marginTop: 16 }}>
        <Space wrap size={[12, 12]}>
          <Button icon={<TeamOutlined />} onClick={() => navigate('/teachers')}>教师与权限</Button>
          <Button icon={<BankOutlined />} onClick={() => navigate('/classes')}>班级管理</Button>
          <Button icon={<UserOutlined />} onClick={() => navigate('/students')}>学生资料</Button>
          <Button icon={<DesktopOutlined />} onClick={() => navigate('/school-admin')}>设备与数据覆盖</Button>
          <Button icon={<BellOutlined />} onClick={() => navigate('/school-operations')}>学校通知</Button>
          <Button icon={<FileDoneOutlined />} onClick={() => navigate('/school-operations')}>学校任务</Button>
          <Button icon={<UserAddOutlined />} onClick={() => navigate('/school-affiliation-corrections/review')}>入校审核</Button>
          <Button icon={<CheckCircleOutlined />} onClick={() => navigate('/invitation-codes')}>邀请码与二维码</Button>
        </Space>
      </Card>
    </div>
  );
}
